import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { useSelector } from 'react-redux';
import { selectImagesDocuments, selectDocumentLoading, selectDocumentError } from '@reducers/v2/imageHandlingV2/imageHandlingV2.slice';
import {
  fetchImageDocumentById,
  fetchImagesDocuments,
  rotateImageDocument,
  removeImageDocument,
  RemoveImageDocumentPayload,
} from '../../../../../reducers/v2/imageHandlingV2/thunks';
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import {
  CropImageDocumentAction,
  ImageContext,
  ImageDocumentStoreItem,
  LoadImagesDocumentsAction,
  RemoveImageDocumentAction,
  RotateImageDocumentAction,
} from '../types/types.d';
import { getImageContextKey } from '@v2/components/imageHandling/helpers';
import { cropImageDocument } from '@reducers/v2/imageHandlingV2/thunks/cropImageDocument';
import { navigationRef } from '@helpers';

interface UseImageHandlingViewModelReturn {
  state: {
    imagesDocuments: ImageDocumentStoreItem[];
    selectedIndex: number;
    setSelectedIndex: Dispatch<SetStateAction<number>>;
    loading: boolean;
    error?: string | null;
  };
  actions: {
    loadImagesDocuments: LoadImagesDocumentsAction;
    removeImageDocument: RemoveImageDocumentAction;
    rotateImageDocument: RotateImageDocumentAction; // optimistic → sync
    cropImageDocument: CropImageDocumentAction; // optimistic → sync
  };
}

export const useImageHandlingViewModel = ({
  imageContext,
  retrieveAllDocuments = true,
}: {
  imageContext: ImageContext;
  retrieveAllDocuments?: boolean;
}): UseImageHandlingViewModelReturn => {
  const dispatch = useAppDispatch();

  const route = navigationRef?.current?.getCurrentRoute();
  const selectedImageKey = route?.params?.selectedImageKey;

  const [selectedIndex, setSelectedIndex] = useState<number>(0);

  // Determine the store key based on params
  const storeKey = getImageContextKey(imageContext);

  const hasValidContext = !!storeKey;

  // Select the specific document store from Redux
  // This will only re-render when this specific document's data changes
  const imagesDocuments = useSelector(selectImagesDocuments(getImageContextKey(imageContext))) as ImageDocumentStoreItem[];
  const loading = useSelector(selectDocumentLoading(storeKey));
  const error = useSelector(selectDocumentError(storeKey));

  const load: LoadImagesDocumentsAction = useCallback(async () => {
    if ('documentId' in imageContext) {
      // Load by documentId
      const result = await dispatch(fetchImageDocumentById(imageContext.documentId));
      if (fetchImageDocumentById.fulfilled.match(result)) {
        const documentData = result.payload;
        if (documentData) {
          // Document loaded successfully
          console.log('Document loaded:', documentData);
        }
      }
    } else if ('scopeId' in imageContext && 'scopeReferenceId' in imageContext) {
      const result = await dispatch(fetchImagesDocuments({ ...imageContext, retrieveAllDocuments }));
      // Load documents by context
      if (fetchImagesDocuments.fulfilled.match(result)) {
        const documentData = result.payload;
        if (documentData) {
          // Documents loaded successfully
          console.log('Documents loaded:', documentData);
        }
      }
    }
  }, [dispatch, imageContext]);

  const imagesDocumentsHash = imagesDocuments.map((i) => i.contentHash).join(', ');

  useEffect(() => {
    const currentSelectedIndex = imagesDocuments.findIndex((i) => i.contentHash === selectedImageKey);
    if (currentSelectedIndex >= 0 && currentSelectedIndex < imagesDocuments.length) {
      setSelectedIndex(currentSelectedIndex);
    }
  }, [imagesDocumentsHash, selectedImageKey]);

  const remove: RemoveImageDocumentAction = useCallback(
    async (payload: RemoveImageDocumentPayload) => {
      dispatch(removeImageDocument(payload));
    },
    [dispatch, storeKey]
  );

  const rotate: RotateImageDocumentAction = useCallback(
    async (params) => {
      dispatch(rotateImageDocument(params));
    },
    [dispatch, storeKey]
  );

  const crop: CropImageDocumentAction = useCallback(
    async (imageDocument: ImageDocumentStoreItem) => {
      dispatch(cropImageDocument({ imageDocument }));
    },
    [dispatch, storeKey]
  );

  return {
    state: {
      imagesDocuments: hasValidContext ? imagesDocuments : [],
      selectedIndex,
      setSelectedIndex,
      loading: hasValidContext ? loading : false,
      error: hasValidContext ? error : null,
    },
    actions: {
      loadImagesDocuments: load,
      removeImageDocument: remove,
      rotateImageDocument: rotate,
      cropImageDocument: crop,
    },
  };
};
